import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, ArrowRight, TrendingUp, ShieldCheck, LogOut, UsersRound, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_hr-tracker-17/artifacts/p39y89ef_LOGO.jpg%20%281%29.jpeg";

const MODULES = [
  {
    id: "team-room",
    href: "/workspace/login",
    title: "Team Room",
    tagline: "HR · Attendance · Cash",
    description: "Mark today's attendance, run the roster, and keep the petty-cash ledger — one login for the whole floor.",
    icon: UsersRound,
    tone: "hsl(var(--brand-teal))",
    illust: CalendarCheck,
    number: "01",
  },
  {
    id: "quotes",
    href: "/quotes/login",
    title: "Quotes",
    tagline: "Sales · Dispatch",
    description: "Build quotations on the go, follow every deal, dispatch on time.",
    icon: Package,
    tone: "hsl(var(--status-leave))",
    illust: TrendingUp,
    number: "02",
  },
];

export default function ModuleChooserPage() {
  const { user, logout } = useAuth();
  const isAdmin = user && user.role === "admin";

  return (
    <div className="min-h-screen grain-bg bloom-bg relative" data-testid="module-chooser">
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
        <header className="flex items-center justify-between anim-fade-up gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3.5" data-testid="hub-brand-link">
            <div
              className="h-12 w-12 rounded-2xl overflow-hidden shadow-soft ring-1 ring-brand/20"
              style={{ background: "hsl(var(--brand-teal))" }}
            >
              <img src={LOGO_URL} alt="SpaceAttire Teams" className="h-full w-full object-cover" />
            </div>
            <div>
              <div className="font-display text-xl font-bold leading-none tracking-tight">
                SpaceAttire Teams
              </div>
            </div>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-mini truncate max-w-[40vw]">
                <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />
                {isAdmin ? "Admin" : "Signed in"} · {user.email}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => logout()}
                data-testid="hub-logout-btn"
                className="h-9 rounded-lg text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                Sign out
              </Button>
            </div>
          )}
        </header>

        <section className="mt-16 sm:mt-24 max-w-3xl anim-fade-up anim-delay-1">
          <div className="eyebrow">
            Welcome
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.02]">
            Which door<br />
            <span className="text-brand italic font-normal">would you like</span>{" "}
            <span className="text-muted-foreground">today?</span>
          </h1>
          <p className="text-base text-muted-foreground mt-6 max-w-lg leading-relaxed">
            Each module runs independently — its own login, its own data. Pick one to continue.
          </p>
        </section>

        <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-testid="module-grid">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            const Illust = m.illust;
            return (
              <Link
                key={m.id}
                to={m.href}
                data-testid={`module-card-${m.id}`}
                className={`group relative overflow-hidden rounded-2xl border border-border bg-card p-7 md:p-8 btn-lift shadow-soft hover:shadow-soft-lg anim-fade-up anim-delay-${i + 2}`}
              >
                <div className="absolute inset-x-0 top-0 h-1" style={{ background: m.tone }} />
                <Illust
                  className="absolute -right-8 -bottom-8 h-40 w-40 opacity-[0.05] pointer-events-none group-hover:opacity-[0.09] transition-opacity duration-500"
                  strokeWidth={1}
                  style={{ color: m.tone }}
                />
                <div className="absolute top-6 right-6 font-mono-mini text-[10px] tracking-[0.3em] text-muted-foreground">
                  {m.number}
                </div>

                <div className="flex items-start">
                  <div
                    className="h-14 w-14 rounded-xl grid place-items-center transition-transform duration-300 group-hover:scale-105 group-hover:rotate-3"
                    style={{ background: `${m.tone}18`, color: m.tone }}
                  >
                    <Icon className="h-7 w-7" strokeWidth={1.8} />
                  </div>
                </div>

                <div className="mt-8">
                  <div className="font-display text-3xl md:text-[2rem] font-bold tracking-tight leading-tight">
                    {m.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1.5 font-medium">{m.tagline}</div>
                </div>

                <p className="mt-5 text-sm text-foreground/75 leading-relaxed">{m.description}</p>

                <div
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all"
                  style={{ color: m.tone }}
                >
                  Enter
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </section>

        {/* Admin-only console tile */}
        {isAdmin && (
          <section className="mt-8 anim-fade-up anim-delay-4" data-testid="admin-console-section">
            <Link
              to="/admin"
              data-testid="admin-console-card"
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-7 btn-lift shadow-soft hover:shadow-soft-lg flex items-center gap-5 flex-wrap"
              style={{ borderColor: "hsl(var(--brand-teal) / 0.35)" }}
            >
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ background: "linear-gradient(90deg, hsl(var(--brand-teal)), hsl(var(--brand-peach)))" }}
              />
              <div
                className="h-14 w-14 rounded-xl grid place-items-center text-white shadow-soft shrink-0"
                style={{ background: "hsl(var(--brand-teal-deep))" }}
              >
                <ShieldCheck className="h-7 w-7" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-2xl font-bold tracking-tight">Admin console</div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-mono-mini bg-brand/10 text-brand font-semibold">
                    Owner
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Approve or reject signup requests, manage users across all modules, delete quotations.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand group-hover:gap-3 transition-all">
                Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </section>
        )}

        <footer className="mt-24 pt-8 border-t border-border flex items-center justify-between text-[11px] uppercase tracking-widest text-muted-foreground font-mono-mini">
          <span>© SpaceAttire Teams · {new Date().getFullYear()}</span>
          <span className="hidden sm:inline">Independent modules · One brand</span>
        </footer>
      </div>
    </div>
  );
}
